import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, GitBranch, Terminal } from "lucide-react";
import { api, type SchemaTrigger } from "@/lib/api";
import { ROUTES } from "@/routes";
import { EmptyState, ErrorBox, PageHeader, Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function DatabaseTriggersPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [triggers, setTriggers] = useState<SchemaTrigger[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void api
      .getSchema(id)
      .then(({ data }) => {
        if (cancelled) return;
        setTriggers(data.triggers ?? []);
        setError(null);
      })
      .catch((nextError) => {
        if (!cancelled) setError(nextError);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <Spinner label="Loading database triggers…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Database"
        title="Database Triggers"
        description="Execute actions automatically when database events occur. This page is read-only; use SQL migrations to change trigger definitions."
        actions={<Badge variant="secondary">{triggers.length} triggers</Badge>}
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {triggers.length === 0 ? (
        <EmptyState title="No database triggers yet" icon={<GitBranch className="h-5 w-5" />}>
          Create a trigger in the SQL editor to see its event definition here.
        </EmptyState>
      ) : (
        <div className="space-y-3" data-testid="database-triggers">
          {triggers.map((trigger) => (
            <Card key={`${trigger.name}-${trigger.table}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="flex items-center gap-2 font-sans text-base">
                      <GitBranch className="h-4 w-4 shrink-0 text-secondary" aria-hidden />
                      <span className="truncate font-mono">{trigger.name}</span>
                    </CardTitle>
                    <CardDescription className="mt-1 flex items-center gap-1.5 text-xs">
                      On table
                      <Link
                        className="inline-flex items-center gap-1 font-mono font-medium text-secondary hover:underline"
                        to={ROUTES.databaseTable(id, trigger.table)}
                      >
                        {trigger.table}
                        <ArrowRight className="h-3 w-3" aria-hidden />
                      </Link>
                    </CardDescription>
                  </div>
                  <Badge variant={trigger.enabled === "D" ? "secondary" : "success"}>
                    {formatTriggerState(trigger.enabled)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <pre className="overflow-x-auto rounded-sm border border-code-block-border bg-code-block-bg p-3 font-mono text-xs leading-5 text-code-block-text">
                  {trigger.definition || "(definition unavailable)"}
                </pre>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`${ROUTES.sql(id)}?table=${encodeURIComponent(trigger.table)}`}>
                    <Terminal className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Open SQL editor
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function formatTriggerState(value: string): string {
  switch (value) {
    case "D":
      return "Disabled";
    case "R":
      return "Replica only";
    case "A":
      return "Always enabled";
    default:
      return "Enabled";
  }
}
